import type { PracticeTestQuestion, QuestionAnswer } from '../services/practiceTestService';

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

interface Props {
  question: PracticeTestQuestion;
  answer: Partial<QuestionAnswer>;
  onAnswer: (update: Partial<QuestionAnswer>) => void;
  questionNumber: number;
}

export default function QuestionRenderer({ question, answer, onAnswer, questionNumber }: Props) {
  const q = question;
  const type = q.question_type || 'mcq_single';

  return (
    <div>
      {/* Section label + marks badge */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {q.section_label && (
            <span className="text-xs font-semibold text-coral-warm bg-orange-50 px-2 py-0.5 rounded-full">
              {q.section_label}
            </span>
          )}
          <span className="text-xs text-gray-400">Q{questionNumber}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-gray-600">{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
          {q.negative_marks > 0 && (
            <span className="text-red-500 font-medium">-{q.negative_marks} wrong</span>
          )}
        </div>
      </div>

      {/* Assertion-Reasoning: show assertion + reason before question */}
      {type === 'assertion_reasoning' && (
        <div className="mb-4 space-y-2">
          {q.assertion_text && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs font-semibold text-blue-700 mb-1">Assertion (A)</p>
              <p className="text-sm text-blue-900">{q.assertion_text}</p>
            </div>
          )}
          {q.reason_text && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs font-semibold text-purple-700 mb-1">Reason (R)</p>
              <p className="text-sm text-purple-900">{q.reason_text}</p>
            </div>
          )}
        </div>
      )}

      {/* Case-Based: show passage/scenario before question */}
      {type === 'case_based' && q.case_study_text && (
        <div className="mb-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
          <p className="text-xs font-semibold text-teal-700 mb-2">Read the following passage carefully</p>
          <p className="text-sm text-teal-900 leading-relaxed whitespace-pre-wrap">{q.case_study_text}</p>
        </div>
      )}

      {/* Question text */}
      <p className="text-base sm:text-lg font-medium text-ocean-deep leading-relaxed mb-6">
        {q.question}
      </p>

      {/* Render input based on type */}
      {(type === 'mcq_single' || type === 'assertion_reasoning') && (
        <MCQSingle question={q} selected={answer.selected ?? null} onSelect={(idx) => onAnswer({ selected: idx })} />
      )}

      {type === 'mcq_multi' && (
        <MCQMulti question={q} selectedOptions={answer.selected_options || []} onSelect={(opts) => onAnswer({ selected_options: opts })} />
      )}

      {type === 'numerical' && (
        <NumericalInput value={answer.numerical_answer ?? null} onChange={(val) => onAnswer({ numerical_answer: val })} />
      )}

      {(type === 'short_answer' || type === 'long_answer' || type === 'case_based') && (
        <TextInput value={answer.text_answer || ''} onChange={(val) => onAnswer({ text_answer: val })} rows={type === 'long_answer' || type === 'case_based' ? 10 : 4} />
      )}
    </div>
  );
}

function MCQSingle({ question, selected, onSelect }: {
  question: PracticeTestQuestion;
  selected: number | null;
  onSelect: (idx: number) => void;
}) {
  const options = [question.option_a, question.option_b, question.option_c, question.option_d].filter(Boolean);

  return (
    <div className="space-y-3">
      {options.map((option, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(idx)}
          className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all duration-200 flex items-start gap-3 ${
            selected === idx
              ? 'border-coral-warm bg-orange-50 text-ocean-deep'
              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold ${
            selected === idx ? 'bg-coral-warm text-white' : 'bg-gray-100 text-gray-500'
          }`}>
            {OPTION_LABELS[idx]}
          </span>
          <span className="text-sm sm:text-base pt-0.5">{option}</span>
        </button>
      ))}
    </div>
  );
}

function MCQMulti({ question, selectedOptions, onSelect }: {
  question: PracticeTestQuestion;
  selectedOptions: number[];
  onSelect: (opts: number[]) => void;
}) {
  const options = [question.option_a, question.option_b, question.option_c, question.option_d].filter(Boolean);

  function toggle(idx: number) {
    if (selectedOptions.includes(idx)) {
      onSelect(selectedOptions.filter(i => i !== idx));
    } else {
      onSelect([...selectedOptions, idx]);
    }
  }

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">Select all correct options</p>
      <div className="space-y-3">
        {options.map((option, idx) => {
          const checked = selectedOptions.includes(idx);
          return (
            <button
              key={idx}
              onClick={() => toggle(idx)}
              className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all duration-200 flex items-start gap-3 ${
                checked
                  ? 'border-coral-warm bg-orange-50 text-ocean-deep'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-sm ${
                checked ? 'bg-coral-warm text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {checked ? '✓' : OPTION_LABELS[idx]}
              </span>
              <span className="text-sm sm:text-base pt-0.5">{option}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NumericalInput({ value, onChange }: {
  value: number | null;
  onChange: (val: number | null) => void;
}) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">Enter numerical answer</p>
      <input
        type="number"
        step="any"
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? null : parseFloat(v));
        }}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base focus:border-coral-warm focus:ring-1 focus:ring-coral-warm outline-none transition-colors"
        placeholder="Type your answer..."
      />
    </div>
  );
}

function TextInput({ value, onChange, rows }: {
  value: string;
  onChange: (val: string) => void;
  rows: number;
}) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">Write your answer below</p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm leading-relaxed focus:border-coral-warm focus:ring-1 focus:ring-coral-warm outline-none transition-colors resize-y"
        placeholder="Type your answer here..."
      />
    </div>
  );
}
